import { Input, Form, Button, Typography } from "antd";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";
import { AppURL } from "utils/Actions";
import { ApiURL } from "utils/Actions";
import api from "ApiClient";
import useQuery from "utils/hooks/useQuery";
import AuthHelper from "components/auth/helpers";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateSchoolContact } from "redux/reducers/SchoolContactSlice";
import { initTimer } from "redux/reducers/LoginTimeOutSlice";
import { setWelcomeModal } from "redux/reducers/PropertiesSlice";
import PasswordRequirementsHelp, {
  PasswordMatch,
  PASSWORD_REQUIREMENTS
} from "components/auth/password-requirements-help";
import style from "./style.module.css";
import { ValidateErrorEntity } from "rc-field-form/lib/interface";

const { Text } = Typography;

interface CreateAccountFormType {
  email?: string;
}
const CreateAccountForm = ({ email = "" }: CreateAccountFormType): JSX.Element => {
  const query = useQuery();
  const history = useHistory();
  const [form] = Form.useForm();
  const { isFieldTouched } = form;
  const password = Form.useWatch("password", form);
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  // need this field, because 'isFieldTouched' is not triggered
  const [forgotToEnterConfirmPassword, setForgotToEnterConfirmPassword] = useState(false);
  const showPasswordsMatch = isFieldTouched("confirm") || forgotToEnterConfirmPassword;
  const confirmPassword = Form.useWatch("confirm", form);

  const onFinish = async (values: any) => {
    const token = query.get("token");
    const a = query.get("a");
    setSubmitting(true);
    const endpoint = `${ApiURL.updatePasswordToken}?t=${token}&a=${a}`;
    try {
      const res = await api.patch(endpoint, {
        password: values.password,
        password2: values.confirm,
        username: email
      });
      const data = await res?.data;
      if (data) {
        dispatch(updateSchoolContact(data));
        AuthHelper.setAuth(data);
        dispatch(initTimer(data.expireTime));
        dispatch(setWelcomeModal(true));
      }
    } catch (error) {
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const onFinishFailed = ({ values }: ValidateErrorEntity) => {
    if (values.confirm === undefined) {
      setForgotToEnterConfirmPassword(true);
    } else {
      setForgotToEnterConfirmPassword(false);
    }
  };

  const onSuccess = () => {
    const auth = AuthHelper.getAuth();
    if (!isEmpty(auth)) {
      history.push(AppURL.school.path);
    }
  };

  useEffect(() => {
    window.addEventListener("storage", onSuccess);
    return () => {
      window.removeEventListener("storage", onSuccess);
    };
  }, [onSuccess]);

  return (
    <Form
      disabled={submitting}
      form={form}
      className={style["form-container"]}
      name="basic"
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      requiredMark={false}
      // prevent errors from showing every time you type
      validateTrigger={["onSubmit"]}
    >
      <div style={{ marginBottom: 24 }}>
        <div>Email</div>
        <Text type="secondary">{email}</Text>
      </div>
      <Form.Item
        label="Create password"
        name="password"
        rules={[
          { required: true, message: "Please input your password." },
          () => ({
            validator(_, value) {
              let valid = true;
              Object.values(PASSWORD_REQUIREMENTS).forEach(pr => {
                if (!value || isEmpty(value.match(pr.regex))) {
                  valid = false;
                }
              });
              if (valid) {
                return Promise.resolve();
              } else {
                return Promise.reject();
              }
            }
          })
        ]}
        style={{ marginBottom: 8 }}
      >
        <Input.Password
          onCopy={e => {
            e.preventDefault();
            return false;
          }}
        />
      </Form.Item>
      <PasswordRequirementsHelp input={password} />

      <Form.Item
        label="Confirm password"
        name="confirm"
        rules={[
          { required: true },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject();
            }
          })
        ]}
        dependencies={["password"]}
        validateTrigger={["onBlur", "onChange"]}
        help={
          <div>
            {showPasswordsMatch ? (
              <PasswordMatch password={password} confirm={confirmPassword} />
            ) : null}
          </div>
        }
      >
        <Input.Password
          onPaste={e => {
            e.preventDefault();
            return false;
          }}
          onCopy={e => {
            e.preventDefault();
            return false;
          }}
        />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" block loading={submitting}>
          Create account
        </Button>
      </Form.Item>
      <Button
        type="link"
        block
        onClick={() => {
          history.push("/");
        }}
      >
        Log in
      </Button>
    </Form>
  );
};

export default CreateAccountForm;
